import { inject, singleton } from 'tsyringe';
import { LoggerService } from '../shared/services/logger.service';
import {
  kNameOfTesterWindowService,
  ITesterWindowService,
} from '../shared/interfaces/tester-window-service.interface';
import { Game } from '../background/models/game.model';
import { Promotion } from '../background/models/promotion.model';

// -----------------------------------------------------------------------------
@singleton()
export class TesterController {
  private readonly logger = LoggerService.getCategory('TesterController');
  private selectedGame: Game;

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kNameOfTesterWindowService)
    private readonly testerWindowService: ITesterWindowService,
  ) {}

  // ---------------------------------------------------------------------------
  public async run() {
    try {
      this.createTesterForm();
    } catch (ex) {
      this.logger.error('Error:', ex);
    }
  }

  // ---------------------------------------------------------------------------
  private async createTesterForm() {
    const form = document.getElementById('tester-form');

    const envInput: HTMLElement = document.querySelector('#environment');
    envInput.onchange = this.handleEnvChange;

    this.AddActionButtons();

    await this.loadGamesFromConfig();
    form.addEventListener('submit', (event: Event) => {
      event.preventDefault();
      if (
        // eslint-disable-next-line dot-notation
        event.currentTarget['promotionId'].value === '' ||
        // eslint-disable-next-line dot-notation
        event.currentTarget['gameId'].value === ''
      ) {
        console.error('[Promotion tester] Missing parameters');
        return;
      }

      // eslint-disable-next-line dot-notation
      const promotionId = event.currentTarget['promotionId'].value;
      const promotion = this.selectedGame.promotions.find(
        (p) => p.id === promotionId,
      );

      const offer = this.selectedGame.offers.find(
        (p) => p.id === promotionId,
      );

      // eslint-disable-next-line dot-notation
      const useDevEnv = event.currentTarget['environment'].value;
      this.testerWindowService.transferTesterPromotion(
        this.selectedGame,
        promotion ?? offer,
        useDevEnv,
      );
    });
  }

  // ---------------------------------------------------------------------------
  private handleGameChange = async (evt) => {
    document
      .querySelectorAll('.promotionSelection')
      .forEach((app) => app.remove());

    const game: Game =
      (await this.testerWindowService.getGameConfiguration(
        parseInt(evt.target.value),
      )) ?? null;

    if (!game) {
      return;
    }

    this.selectedGame = game;
    this.loadPromosFromConfig(game);
  };

  // ---------------------------------------------------------------------------
  private handleEnvChange = async (event) => {
    await this.testerWindowService.setEnvironment(event.target.value);
    await this.loadGamesFromConfig();
  };

  // ---------------------------------------------------------------------------
  private loadPromosFromConfig(game: Game) {
    const promoInput = document.getElementById('promotionId');
    document.querySelectorAll('.promotionSelection').forEach((item) => {
      item.remove();
    });

    game.promotions.sort((a, b) => {
      return this.sort(a.name, b.name);
    });

    for (const promotion of game.promotions) {
      const option = document.createElement('option');
      option.setAttribute('value', promotion.id);
      option.setAttribute('class', 'promotionSelection');
      option.textContent = `${promotion.name} - ${promotion.id}`;
      promoInput.appendChild(option);
    }

    for (const promotion of game.offers) {
      const option = document.createElement('option');
      option.setAttribute('value', promotion.id);
      option.setAttribute('class', 'promotionSelection');
      option.textContent = `${promotion.name} - ${promotion.id}`;
      promoInput.appendChild(option);
    }
  }

  // ---------------------------------------------------------------------------
  private async loadGamesFromConfig() {
    const gameInput = document.getElementById('gameId');
    gameInput.onchange = this.handleGameChange;
    const games: Game[] =
      await this.testerWindowService.getAllGamesConfigurations();
    document.querySelectorAll('.game').forEach((item) => {
      item.remove();
    });

    games?.sort((a, b) => {
      return this.sort(a.name, b.name);
    });

    if (!games || games.length === 0) {
      return;
    }

    for (const game of games) {
      const option = document.createElement('option');
      option.setAttribute('value', game.id.toString());
      option.setAttribute('class', 'game');
      option.textContent = game.name;
      gameInput.appendChild(option);
    }

    gameInput.dispatchEvent(new Event('change'));
  }

  // ---------------------------------------------------------------------------
  private AddActionButtons() {
    this.addClearDisplayedPromotions();
    this.addResetLocalStorageBtn();
    this.addResetPromotionsHistoryBtn();
    this.addSimulateAdUnitView();
  }

  // ---------------------------------------------------------------------------
  private addResetPromotionsHistoryBtn() {
    const clearPromotionHistory = document.getElementById(
      'resetPromotionsHistory',
    );

    clearPromotionHistory.addEventListener('click', () => {
      this.testerWindowService.resetPromotionsHistory();
    });
  }

  // ---------------------------------------------------------------------------
  private addResetLocalStorageBtn() {
    const clearLocalStorageBtn = document.getElementById('resetLocalStorage');

    clearLocalStorageBtn.addEventListener('click', () => {
      this.testerWindowService.resetLocalStorage();
    });
  }

  // ---------------------------------------------------------------------------
  private addClearDisplayedPromotions() {
    const clearConditionsSummaryBtn = document.getElementById(
      'clearDisplayedPromotions',
    );

    clearConditionsSummaryBtn.addEventListener('click', () => {
      this.testerWindowService.resetDisplayedPromotions();
    });
  }

  private addSimulateAdUnitView() {
    const simulateAdView = document.getElementById('simulateAdView');
    simulateAdView.addEventListener('click', () => {
      this.simulateAdUnitView();
    });
  }

  // ---------------------------------------------------------------------------
  private simulateAdUnitView() {
    const comsIframe: HTMLIFrameElement =
      document.querySelector('#coms-iframe');
    comsIframe.src =
      'overwolf-extension://bgahplehcohebablohmbilgjdnihhgfmfepkpgcj/dist/coms/coms.html?promotionId=FROVERWOLF1';
  }

  // ---------------------------------------------------------------------------
  private sort(a: string, b: string) {
    if (a > b) {
      return 1;
    }

    if (a < b) {
      return -1;
    }

    return 0;
  }
}
